/**
 * use-conversation-versions — TanStack Query hooks for the P2 conversation
 * version endpoints.
 *
 * conversation-model-versioning Phase 5 Session 2 (2026-05-17).
 * ADR 0028 (2026-05-18): restore is now destructive — no safety version is
 * auto-created. `ApiRestoreVersionResponse.data` carries only `restoredVersionId`.
 *
 * Four hooks against the frozen P2 contracts:
 * - `useConversationVersions(convId)` — GET list; frozen-direct metrics +
 *   `staleVsCurrentModel` per row.
 * - `useCreateVersion(convId)` — POST create; surfaces 422 `INCOMPLETE_MODEL`
 *   (with `unaddressed` list from error body) + 409 `RUN_IN_PROGRESS`.
 * - `useRestoreVersion(convId)` — POST restore; returns `restoredVersionId`;
 *   409 `RUN_IN_PROGRESS`. Restore is a destructive overwrite — no safety
 *   snapshot is created (ADR 0028).
 * - `useVersionDiff(convId, from, to)` — GET diff; `to` defaults to `'live'`
 *   per the P2 contract.
 *
 * Invalidation: `useCreateVersion` and `useRestoreVersion` invalidate
 * `queryKeys.conversations.all` on success, mirroring the `useForkShare`
 * precedent (`use-shares.ts`) — this refreshes the pipeline projection +
 * the versions list in one sweep.
 *
 * Error surfacing: mutations throw `ApiError` (from `lib/api.ts`) with
 * `error.code` set to `'INCOMPLETE_MODEL'` or `'RUN_IN_PROGRESS'`. The
 * `ApiError` constructor carries `details` for the 422 unaddressed list.
 * Consumers narrow via `error instanceof ApiError && error.code === '...'`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { VersionDiffResponse } from '@arcanum-hq/shared-types';

// ---------------------------------------------------------------------------
// Wire shapes for the P2 version endpoints
// ---------------------------------------------------------------------------

/**
 * A single item from `GET /api/conversations/:id/versions`.
 * Frozen-direct: metrics come from the version row's `metrics` blob, keyed by
 * output `name` (e.g. `'IRR'`, `'StabilizedDSCR'`). Absent metric → server
 * returns `'—'`; web renders as-is (no further parsing).
 */
export interface ConversationVersionItem {
  id: string;
  label: string;
  seq: number;
  // Wire shape is snake_case — matches the published OpenAPI `VersionListItem`
  // schema (`conversations.schema.ts`: `version_kind`, `created_at`). The
  // earlier camelCase declaration silently produced "Invalid Date" in
  // <BrowseVersionsModal> because `version.createdAt` was always `undefined`.
  version_kind: 'manual' | 'safety';
  created_at: string;
  /** Frozen metrics blob keyed by output name. Absent key → server omits or returns '—'. */
  metrics: Record<string, string | number | null>;
  /**
   * True when the version's `metrics_captured_at` predates the conversation's
   * current `model_version` — i.e. the frozen snapshot was computed against
   * an older model schema.
   */
  staleVsCurrentModel: boolean;
}

/** Response from `GET /api/conversations/:id/versions`. */
interface ApiVersionListResponse {
  success: boolean;
  data: ConversationVersionItem[];
}

/** Response from `POST /api/conversations/:id/versions`. */
interface ApiCreateVersionResponse {
  success: boolean;
  data: {
    id: string;
    label: string;
    seq: number;
    version_kind: 'manual' | 'safety';
    created_at: string;
  };
}

/** Response from `POST /api/conversations/:id/versions/:vid/restore`. */
interface ApiRestoreVersionResponse {
  success: boolean;
  data: {
    /** ID of the version that was restored to. ADR 0028: no safetyVersionId. */
    restoredVersionId: string;
  };
}

/** Response from `GET /api/conversations/:id/versions/diff`. */
interface ApiVersionDiffResponse {
  success: boolean;
  data: VersionDiffResponse;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch the ordered version list for a conversation (newest first).
 *
 * Query key: `queryKeys.conversations.versions(convId)` — nested under
 * `conversations.detail(convId)` so the ADR-0020 `conversations.all`
 * invalidation sweeps it automatically (Session 8).
 *
 * Disabled when `convId` is falsy (no active conversation).
 */
export function useConversationVersions(convId: string | null | undefined) {
  return useQuery<ConversationVersionItem[], ApiError>({
    queryKey: queryKeys.conversations.versions(convId ?? ''),
    queryFn: async (): Promise<ConversationVersionItem[]> => {
      const res = await api.get<ApiVersionListResponse>(
        `/api/conversations/${convId}/versions`
      );
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!convId,
  });
}

/**
 * Create a manual version snapshot for a conversation.
 *
 * Throws `ApiError` with:
 * - `status: 422`, `code: 'INCOMPLETE_MODEL'` — completeness gate failed.
 *   `error.details` carries `{ unaddressed: { displayName: string; isLocation: boolean }[] }`.
 * - `status: 409`, `code: 'RUN_IN_PROGRESS'` — a streaming run is active.
 *
 * On success: invalidates `queryKeys.conversations.all` (refreshes the
 * pipeline projection + the versions list in one sweep — mirrors the
 * `useForkShare` precedent).
 *
 * `retry: 0` because version creation is idempotency-sensitive; the user
 * re-submits via the UI, not a silent retry.
 */
export function useCreateVersion(convId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    ApiCreateVersionResponse['data'],
    ApiError,
    { label?: string }
  >({
    mutationFn: async (body: { label?: string }) => {
      if (!convId) {
        throw new ApiError(
          400,
          'Cannot create version: no active conversation'
        );
      }
      const res = await api.post<ApiCreateVersionResponse>(
        `/api/conversations/${convId}/versions`,
        body
      );
      return res.data;
    },
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all,
      });
    },
  });
}

/**
 * Restore a specific version as the conversation's current state.
 *
 * ADR 0028: restore is a destructive, irreversible overwrite of the current
 * working state. No safety snapshot is auto-created. The returned
 * `restoredVersionId` identifies the version that was restored to.
 *
 * Throws `ApiError` with:
 * - `status: 409`, `code: 'RUN_IN_PROGRESS'` — a streaming run is active.
 *
 * On success: invalidates `queryKeys.conversations.all` (refreshes the
 * pipeline projection + the versions list).
 *
 * `retry: 0` for the same idempotency rationale as `useCreateVersion`.
 */
export function useRestoreVersion(convId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    ApiRestoreVersionResponse['data'],
    ApiError,
    { versionId: string }
  >({
    mutationFn: async ({ versionId }: { versionId: string }) => {
      if (!convId) {
        throw new ApiError(
          400,
          'Cannot restore version: no active conversation'
        );
      }
      const res = await api.post<ApiRestoreVersionResponse>(
        `/api/conversations/${convId}/versions/${versionId}/restore`,
        {}
      );
      return res.data;
    },
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all,
      });
    },
  });
}

/**
 * Fetch a diff between two version refs for a conversation.
 *
 * `from` and `to` are version refs: a version `seq` number (as string),
 * a version `id` (UUID), or the literal `'live'` (current agent state).
 * Default `to='live'` per the P2 contract — covers the common "what changed
 * since I saved this version?" use case.
 *
 * Query key: `queryKeys.conversations.versionDiff(convId, from, to)` —
 * nested under `conversations.detail(convId)` for the ADR-0020 sweep.
 *
 * Disabled when `convId` or `from` is falsy.
 */
export function useVersionDiff(
  convId: string | null | undefined,
  from: string | null | undefined,
  to = 'live'
) {
  return useQuery<VersionDiffResponse, ApiError>({
    queryKey: queryKeys.conversations.versionDiff(convId ?? '', from ?? '', to),
    queryFn: async (): Promise<VersionDiffResponse> => {
      const params = new URLSearchParams({ from: from!, to });
      const res = await api.get<ApiVersionDiffResponse>(
        `/api/conversations/${convId}/versions/diff?${params.toString()}`
      );
      return res.data;
    },
    enabled: !!convId && !!from,
  });
}
